<template>
	<FormCard class="">
		<h3>Step Breakdown - per {{ calculationOptions?.outputQuantity }} {{ selectedUnit?.unitName }} of {{ outputDescription }}</h3>
		<ResultGridFilterBadge :visible-count="visibleRows.length" :total-count="lifeCycleResultList.length" :is-filtered="isFiltered" @clear="clearAllFilters" />
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="lifeCycleResultList"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="resultGridDefaultColDef"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
				@row-data-updated="refreshVisibleRows"
				@model-updated="refreshVisibleRows"
				@filter-changed="refreshVisibleRows"
				@sort-changed="refreshVisibleRows"
				@first-data-rendered="refreshVisibleRows"
			>
			</ag-grid-vue>
		</div>

		<!-- Chart Type Toggle -->
		<div class="flex mt-2 mb-2 justify-center">
			<div class="flex items-center space-x-4">
				<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
					<div
						class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
					></div>
				</label>
				<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
				<label v-show="!showPieChart" v-tooltip.top="'Display charts in a scrollable container with fixed height. Useful when there are many items.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-4">
					<input type="checkbox" v-model="scrollMode" class="accent-blue-600" />
					Scroll
				</label>
				<label v-show="!showPieChart" v-tooltip.top="'Widen each chart (fewer charts per row) for a clearer visualization.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-2">
					<input type="checkbox" v-model="extendMode" class="accent-blue-600" />
					Extend
				</label>
			</div>
		</div>
		<!-- Dynamic Charts Component with CSS Grid Layout -->
		<div :class="['grid gap-4 mt-4', extendMode ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3']">
			<DynamicChartComponent
				v-for="emissionFactor in props.activeEmissionFactors"
				:key="emissionFactor.id"
				:title="
					props.calculationOptions?.useWeightedEmissionFactors
						? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]')
						: emissionFactor.emissionFactorName
				"
				:emissionFactors="emissionFactor"
				:chartData="chartDataItems"
				:showPieChart="showPieChart"
				:scrollMode="scrollMode"
				class="min-h-96 flex flex-col"
			/>
		</div>

		<!-- <div>
			<div>
				<vue-json-pretty :data="lifeCycleResultList" />
			</div>
		</div> -->
	</FormCard>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, nextTick, watch } from "vue"
import { ApiClient, CalculationOptions, EmissionFactors, LifeCycleResult, Step, Unit } from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faFloppyDisk, faXmarkLarge } from "@fortawesome/pro-regular-svg-icons"
import DynamicChartComponent from "./DynamicChartComponent.vue"
import type { ChartDataItem } from "./DynamicChartComponent.vue"
import ResultGridFilterBadge from "./ResultGridFilterBadge.vue"
//import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js"
//ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale)

import { GridApi, GridReadyEvent, ValueGetterParams, ColDef, CellStyle } from "ag-grid-community"
const gridApi = ref<GridApi | null>(null)

// Computed property to map the currently visible (filtered) rows to ChartDataItem format
const chartDataItems = computed((): ChartDataItem[] => {
	return visibleRows.value.map((item) => ({
		name: item.name || "",
		emissionFactors: item.emissionFactors || {},
	}))
})

const pinnedBottomRow = computed(() => {
	if (!visibleRows.value.length) return []

	const totals = {
		stepNumber: "Grand Total",
		climateChange: 0,
		waterConsumption: 0,
		water: 0,
		solvent: 0,
		total: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	for (const row of visibleRows.value) {
		totals.climateChange += row.climateChange ?? 0
		totals.waterConsumption += row.waterConsumption ?? 0
		totals.water += row.water ?? 0
		totals.solvent += row.solvent ?? 0
		totals.total += row.total ?? 0
		if (row.emissionFactors) {
			for (const ef of props.activeEmissionFactors) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
	}

	return [totals]
})

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
	refreshVisibleRows()
}

library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge)

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	activeEmissionFactors: {
		type: Array<EmissionFactors>,
		required: false,
		default: () => [],
	},
	calculationOptions: {
		type: CalculationOptions,
		required: false,
		default: undefined,
	},
	selectedStep: {
		type: Step,
		required: false,
		default: undefined,
	},
	selectedUnit: {
		type: Unit,
		required: false,
		default: undefined,
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	steps: {
		type: Array as () => Step[],
		required: false,
		default: () => [],
	},
})

import { useStepOutputDescription } from "@/composables/stepOutputDescription"
const outputDescription = useStepOutputDescription(
	computed(() => props.tabName),
	computed(() => props.selectedStep),
	computed(() => props.steps as Step[])
)

function getResults(generalInfomationId: string) {
	getResultsByStep(generalInfomationId)
}

let toast = useToast()
const displayResultByStep = ref(false)
const displayLifeCycleResultListGraph1 = ref(false)

const lifeCycleResultList = ref([] as LifeCycleResult[])

// Rows currently visible in the grid after filtering - drives the charts and the grand total row
const { visibleRows, isFiltered, refreshVisibleRows, clearAllFilters } = useGridFilterSync<LifeCycleResult>(gridApi, lifeCycleResultList)

const showPieChart = ref(true)
const scrollMode = ref(false)
const extendMode = ref(false)
watch(
	() => chartDataItems.value.length,
	(newLen) => {
		scrollMode.value = newLen > 7
	}
)

import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	resultGridDefaultColDef,
	textFilterColDef,
	numberFilterColDef,
	BuildMinMaxValues,
	ColumnMinMax,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"
import { useGridFilterSync } from "../composables/useGridFilterSync"
import { generateChartColorsFromField } from "../composables/chartColorHelper"
const columnMinMax = ref<Array<ColumnMinMax>>([])

const agGridColumnDefs = computed(() => [
	{
		field: "stepNumber",
		headerName: "#",
		valueFormatter: (p: any) => (typeof p.value === "number" || typeof p.value == "string" ? p.value : ""),
		pinned: "left" as const,
		filter: false,
	},
	{ field: "name", headerName: "Step", pinned: "left" as const, ...textFilterColDef },
	...(props.calculationOptions?.useWeightedEmissionFactors
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: any) => valueToStringFormatter(p.value),
					aggFunc: "sum",
					...numberFilterColDef,
				},
			]
		: []),
	...props.activeEmissionFactors.map<ColDef>((ef) => ({
		field: ef.emissionFactorName,
		headerName: props.calculationOptions?.useWeightedEmissionFactors ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		valueGetter: (p: any) => {
			const value = ef.emissionFactorName == null || !p.data?.emissionFactors ? null : (p.data?.emissionFactors[ef.emissionFactorName] ?? null)
			return value
		},
		valueFormatter: (p: any) => valueToStringFormatter(p.value),
		cellStyle: (params: any): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { textAlign: "right", backgroundColor: "transparent" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					backgroundColor: getConditionalFormatColor(params.value, minMax.min, minMax.max),
					textAlign: "right",
				}
			}
			return null
		},
		aggFunc: "sum",
		...numberFilterColDef,
	})),
])

onMounted(() => {})

function getResultsByStep(generalInformationId: string) {
	if (generalInformationId != null) {
		apiClient.getProductProcessResultsByStep(generalInformationId, props.calculationOptions).then(
			(res) => {
				if (res && res != null && res.length > 0) {
					const cleaned = res.filter((obj) => obj.name !== "Total")

					lifeCycleResultList.value = !res.some((obj) => Object.values(obj).includes(undefined))
						? cleaned.sort((a, b) => a.stepNumber! - b.stepNumber!)
						: cleaned

					displayResultByStep.value = true
					displayLifeCycleResultListGraph1.value = true
					var emissionFactors = lifeCycleResultList.value.map((item) => item.emissionFactors ?? {})
					columnMinMax.value = BuildMinMaxValuesFromEmissionFactors(emissionFactors)
				}
			},
			(err) => {
				//67441
				//toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				if (err.status == 400) {
					toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
					console.error("Error Id:", err.response.errorId)
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				}
			}
		)
	}
}

const getGridApi = () => {
	return gridApi.value
}

defineExpose({
	getResults,
	getGridApi,
	getResultData: () => lifeCycleResultList.value,
})
</script>
<style></style>
